import FloatingLabel from 'react-bootstrap/FloatingLabel';
import { useEffect, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { DropField } from '../../elements/general';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import useUrlParams from '../../../hooks/useUrlParams';
import { useTranslation } from 'react-i18next';
import { setPaginationMaxPage } from '../../../redux/slices/meQrData';
import { Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import {
  buildQrDataUrl,
  mergePageItems,
  shouldLoadNextPage
} from './QrFilter.pagination';

export const getQrData = async ({
  baseUrl,
  page,
  isMePage,
  limit,
  search,
  signal,
  throwOnError = false
}) => {
  try {
    const { data } = await axios(
      buildQrDataUrl({ baseUrl, page, isMePage, limit, search }),
      {
        withCredentials: true,
        signal
      }
    );
    if (data.error) {
      throw new Error(data.error);
    }
    return data;
  } catch (err) {
    if (err.code === 'ERR_CANCELED') return null;

    if (throwOnError) throw err;

    return { [isMePage ? 'items' : 'entries']: [], totalPage: 1 };
  }
};

const getPageLinksCount = (page) => {
  if (typeof page?.linksCount !== 'number') return null;

  return page.linksCount;
};

const QrFilter = ({
  selectedQrs,
  setSelectedQrs,
  setSelectedPageLinksCount,
  setSelectedFolderId,
  onPageReset
}) => {
  const { isFitersActive, page, selectedFolderId } = useSelector(
    (state) => ({
      isFitersActive: state.filters.isFitersActive,
      page: state.meQrData.pagination.page,
      selectedFolderId: state.meQrData.folderId
    })
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [qrs, setQrs] = useState([]);
  const [pageListPage, setPageListPage] = useState(1);
  const [pageListMaxPage, setPageListMaxPage] = useState(1);
  const [isAllPagesLoaded, setIsAllPagesLoaded] = useState(false);
  const [filteredQrs, setFilteredQrs] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isShowFolderTitle, setIsShowFolderTitle] = useState(false);
  const [isActiveDropdown, setIsActiveDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const { baseUrl, id, isMePage, isMeShortLinkPage, isGenQrCodePage } = useUrlParams();

  const folders = qrs.reduce((acc, item) => {
    if (item.folderId && !acc.map((el) => el.id).includes(item.folderId)) {
      acc.push({ name: item.folderName, id: item.folderId });
    }
    return acc;
  }, []);

  useEffect(() => {
    if (!isMePage) return undefined;

    const timer = setTimeout(() => {
      setSearchQuery(searchValue.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [isMePage, searchValue]);

  useEffect(() => {
    let isCurrentRequest = true;
    const controller = new AbortController();

    if (!isActiveDropdown && !id) {
      return;
    }

    if (isMePage && (isAllPagesLoaded || (qrs.length && pageListPage === 1))) {
      return;
    }

    setLoading(true);
    const requestPage = isMePage ? pageListPage : page;

    getQrData({
      baseUrl,
      page: requestPage,
      isMePage,
      search: isMePage ? searchQuery : undefined,
      signal: controller.signal
    }).then((data) => {
      if (!isCurrentRequest || !data) return;

      setLoading(false);
      const items = (isMePage ? data.items : data.entries) ?? [];

      if (isMePage) {
        setQrs((currentQrs) =>
          mergePageItems(currentQrs, items, pageListPage)
        );
        setPageListMaxPage(data.totalPage);
      } else {
        setQrs(items);
        dispatch(setPaginationMaxPage(data.totalPage));
      }
    });
    // eslint-disable-next-line
    return () => {
      isCurrentRequest = false;
      controller.abort();
    };
    // eslint-disable-next-line
  }, [
    page,
    pageListPage,
    isActiveDropdown,
    isAllPagesLoaded,
    searchQuery
  ]);

  const loadAllPages = async () => {
    if (!isMePage || isAllPagesLoaded || !qrs.length) return qrs;

    setLoading(true);

    try {
      let allPages = qrs;

      for (
        let page = pageListPage + 1;
        page <= pageListMaxPage;
        page += 1
      ) {
        const data = await getQrData({
          baseUrl,
          page,
          isMePage,
          throwOnError: true
        });

        allPages = mergePageItems(allPages, data.items ?? [], page);
      }

      setQrs(allPages);
      setPageListPage(pageListMaxPage);
      setIsAllPagesLoaded(true);

      return allPages;
    } catch (error) {
      console.error('Failed to load all pages for folder selection', error);

      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleMenuScroll = (event) => {
    if (!shouldLoadNextPage({
      isMePage,
      loading,
      currentPage: pageListPage,
      maxPage: pageListMaxPage,
      scrollHeight: event.currentTarget.scrollHeight,
      scrollTop: event.currentTarget.scrollTop,
      clientHeight: event.currentTarget.clientHeight
    })) {
      return;
    }

    setPageListPage((currentPage) => currentPage + 1);
  };

  useEffect(() => {
    if (selectedFolderId === null) {
      setIsShowFolderTitle(false);
    }
    // eslint-disable-next-line
  }, [selectedFolderId]);

  const handleQrSelect = (qrId) => {
    setSelectedQrs([qrId]);
    const page = qrs.find((item) => item.entryId === qrId);
    const linksCount = getPageLinksCount(page);
    setSelectedPageLinksCount(linksCount);
    setSelectedFolderId(null);
    setIsShowFolderTitle(false);
  };

  const handleQrSelectFolder = async (folderId) => {
    const allPages = await loadAllPages();
    if (!allPages) return;

    const ids = allPages.reduce((acc, item) => {
      if (item.folderId === folderId) {
        acc.push(item.entryId);
      }
      return acc;
    }, []);
    setSearchValue('');
    setSelectedQrs(ids);
    setSelectedPageLinksCount(null);
    setSelectedFolderId(folderId);
    setIsShowFolderTitle(true);
  };

  const handleResetQr = () => {
    setSearchValue('');
    setSelectedQrs([]);
    setSelectedPageLinksCount(null);
    setSelectedFolderId(null);
    setIsShowFolderTitle(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('id');
    window.history.replaceState({}, document.title, url.toString());
    onPageReset?.();
  };
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchValue(value);

    if (isMePage) {
      setQrs([]);
      setPageListPage(1);
      setPageListMaxPage(1);
      setIsAllPagesLoaded(false);
      return;
    }

    setFilteredQrs(
      qrs.filter((item) => item.entryTitle.toLowerCase().includes(value))
    );
  };
  useEffect(() => {
    if (!isFitersActive) {
      setSelectedQrs([]);
    }
    // eslint-disable-next-line
  }, [isFitersActive]);

  useEffect(() => {
    if (id) {
      setSelectedQrs([Number(id)]);
    }
    // eslint-disable-next-line
  }, [id]);

  const filterTitle = isMePage
    ? t('mePage.pageTitle')
    : isMeShortLinkPage
      ? t('filters.linkTitle')
      : t('filters.qrTitle');
  const allDataTitle = isMePage
    ? t('mePage.allPages')
    : isMeShortLinkPage
      ? t('filters.allLinks')
      : t('filters.allCodes');

  return (
    <DropField onClick={() => setIsActiveDropdown(true)}>
      <FloatingLabel
        label={isShowFolderTitle ? t('filters.foldersTitle') : filterTitle}
        className={isGenQrCodePage && 'gen-qr-floating gen-qr-form-floating'}
      >
        <Dropdown.Toggle className="form-select">
          <div className="me-3 overflow-hidden text-truncate">
            {isShowFolderTitle
              ? qrs.find((item) => item.entryId === selectedQrs[0])?.folderName
              : selectedQrs.length === 1
                ? qrs.find((item) => item.entryId === selectedQrs[0])?.entryTitle
                : allDataTitle}
          </div>
        </Dropdown.Toggle>
      </FloatingLabel>

      <Dropdown.Menu onScroll={handleMenuScroll}>
        <div className="px-1">
          <Form.Control
            className="border-0 shadow-none"
            type="text"
            placeholder={`${t('filters.search')}...`}
            onChange={handleSearch}
            value={searchValue}
            disabled={loading && !qrs.length}
            onKeyDown={(e) => {
              if (e.key === ' ' && searchValue === '') {
                e.preventDefault();
              }
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </div>
        <Dropdown.Divider />
        {isMePage && !searchValue && !isAllPagesLoaded && qrs.length > 0 && (
          <Dropdown.Item onClick={loadAllPages} className="fw-bold">
            {t('filters.folders')}
          </Dropdown.Item>
        )}
        {(!isMePage || isAllPagesLoaded) && folders.length > 0 && (
          <>
            <Dropdown.Item
              onClick={handleResetQr}
              className={`fw-bold ${
                isMePage ? 'bg-secondary2' : ' bg-secondary'
              }
                ${ isGenQrCodePage ? 'bg-secondary-gen-qr' : ' bg-secondary' }
              `}
            >
              {t('filters.folders')}
            </Dropdown.Item>
            {folders.map((item) => (
              <Dropdown.Item
                key={item.id}
                onClick={() => handleQrSelectFolder(item.id)}
              >
                {item.name}
              </Dropdown.Item>
            ))}
            <Dropdown.Divider />
          </>
        )}
        <Dropdown.Item
          onClick={handleResetQr}
          className={`fw-bold
            ${isMePage ? 'bg-secondary2' : ' bg-secondary'}
            ${ isGenQrCodePage ? 'bg-secondary-gen-qr' : ' bg-secondary' }
          `}
        >
          {allDataTitle}
        </Dropdown.Item>
        {loading && !qrs.length ? (
          <div className="d-flex justify-content-center my-2">
            <Spinner animation="border" role="status" className="m-auto" />
          </div>
        ) : searchValue.trim() ? (
          (isMePage ? qrs : filteredQrs).length > 0 ? (
            (isMePage ? qrs : filteredQrs).map((item) => (
              <Dropdown.Item
                key={item.entryId}
                onClick={() => handleQrSelect(item.entryId)}
              >
                {item.entryTitle}
              </Dropdown.Item>
            ))
          ) : (
            <div className="text-center text-muted my-2">
              {t('filters.noMatches')}
            </div>
          )
        ) : (
          qrs.map((item) => (
            <Dropdown.Item
              key={item.entryId}
              onClick={() => handleQrSelect(item.entryId)}
            >
              {item.entryTitle}
            </Dropdown.Item>
          ))
        )}
        {isMePage && loading && qrs.length > 0 && (
          <div className="d-flex justify-content-center my-2">
            <Spinner animation="border" role="status" />
          </div>
        )}
      </Dropdown.Menu>
    </DropField>
  );
};

export default QrFilter;
